import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  parseOrderingErrorBody,
  parseOrderingParam,
  type TableSortState,
  toggleSortState,
  toOrderingParam,
} from '../../utils/listOrdering';
import { ADMIN_CALLS_PAGE_SIZE, readAdminCallsPage } from './adminCallsModel';
import {
  ADMIN_CALL_FIELD_TO_SORT_KEY,
  ADMIN_CALL_SORT_KEY_TO_FIELD,
  type AdminCallSortKey,
  PA_URL_CALLS,
} from './platformAdminOrdering';

const NO_SORT: TableSortState<AdminCallSortKey> = {
  key: null,
  direction: 'desc',
};

/**
 * Calls (Admin) list state. Called by PlatformAdmin rather than the tab so
 * page and rows survive switching to another tab and back.
 */
export function useAdminCalls(active: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [calls, setCalls] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  const sort: TableSortState<AdminCallSortKey> = useMemo(
    () =>
      parseOrderingParam(
        searchParams.get(PA_URL_CALLS),
        ADMIN_CALL_FIELD_TO_SORT_KEY,
      ) ?? NO_SORT,
    [searchParams],
  );

  const orderingApi = useMemo(
    () => toOrderingParam(sort, ADMIN_CALL_SORT_KEY_TO_FIELD),
    [sort],
  );

  const toggleSort = useCallback(
    (key: AdminCallSortKey) => {
      if (!ADMIN_CALL_SORT_KEY_TO_FIELD[key]) return;
      setPage(1);
      setListError(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const current =
          parseOrderingParam(
            prev.get(PA_URL_CALLS),
            ADMIN_CALL_FIELD_TO_SORT_KEY,
          ) ?? NO_SORT;
        const toggled = toggleSortState(current, key);
        const param = toOrderingParam(toggled, ADMIN_CALL_SORT_KEY_TO_FIELD);
        if (param) next.set(PA_URL_CALLS, param);
        else next.delete(PA_URL_CALLS);
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1);
    setListError(null);
  };

  const fetchCalls = useCallback(
    async (pageToLoad = 1) => {
      setLoading(true);
      setListError(null);
      try {
        const response = await api.get(
          apiEndpoints.getAdminCalls(
            pageToLoad,
            ADMIN_CALLS_PAGE_SIZE,
            orderingApi ?? undefined,
            debouncedSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const result = readAdminCallsPage(await response.json());
          setCalls(result.calls);
          if (result.count !== undefined) setCount(result.count);
          if (result.totalPages !== undefined) {
            setTotalPages(result.totalPages);
          }
        } else if (response?.status === 400) {
          const body = await response.json().catch(() => ({}));
          setListError(parseOrderingErrorBody(body) || 'Could not apply sort.');
          setCalls([]);
          setCount(0);
          setTotalPages(1);
        } else {
          setCalls([]);
        }
      } catch {
        setCalls([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, orderingApi],
  );

  useEffect(() => {
    if (active) {
      fetchCalls(page);
    }
  }, [active, page, fetchCalls]);

  return {
    calls,
    loading,
    page,
    totalPages,
    count,
    search,
    appliedSearch: debouncedSearch,
    listError,
    sort,
    setPage,
    onSearchChange,
    toggleSort,
  };
}

export type AdminCallsState = ReturnType<typeof useAdminCalls>;
